
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService.js';
import FaceIDScanner from '../../components/admin/FaceIDScanner';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isTwoFactorRequired, setIsTwoFactorRequired] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [pendingUser, setPendingUser] = useState<Record<string, any> | null>(null);
  const [isFaceIdRequired, setIsFaceIdRequired] = useState(false);
  const [faceIdToken, setFaceIdToken] = useState('');
  const [isFaceIdScanning, setIsFaceIdScanning] = useState(false);
  const [faceIdBackupCode, setFaceIdBackupCode] = useState('');
  const [useFaceIdBackupCode, setUseFaceIdBackupCode] = useState(false);
  const [hasTwoFactorBackup, setHasTwoFactorBackup] = useState(false);
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();

  const handleOtpDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const currentOtp = otpCode.split('');

    while (currentOtp.length < 6) {
      currentOtp.push('');
    }

    currentOtp[index] = digit;
    const newOtp = currentOtp.join('').replace(/\s+/g, '');
    setOtpCode(newOtp);

    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    } else if (!digit && index > 0) {
      setTimeout(() => otpInputRefs.current[index - 1]?.focus(), 0);
    }
  };

  const handleOtpKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && (!otpCode[index] || otpCode.length < index + 1)) {
      if (index > 0) {
        otpInputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;
    const digits = pasted.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digits);
    const nextIndex = Math.min(digits.length, 5);
    setTimeout(() => otpInputRefs.current[nextIndex]?.focus(), 0);
  };

  const handleBackupCodeChange = (value: string) => {
    setBackupCode(value.toUpperCase().replace(/\s+/g, ''));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  useEffect(() => {
    if (isTwoFactorRequired && !useBackupCode) {
      otpInputRefs.current[0]?.focus();
    }
  }, [isTwoFactorRequired, useBackupCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await authService.login(formData);

      // Kiểm tra Face ID requirement TRƯỚC (ưu tiên)
      if (result && 'requiresFaceId' in result && result.requiresFaceId) {
        setIsFaceIdRequired(true);
        setFaceIdToken(result.faceIdToken);
        setPendingUser(result.user || null);
        setIsFaceIdScanning(true);
        setHasTwoFactorBackup(result.hasTwoFactorBackup || false);
        setError('');
        return;
      }

      // Kiểm tra 2FA requirement (nếu không có Face ID)
      if (result && 'requiresTwoFactor' in result && result.requiresTwoFactor) {
        setIsTwoFactorRequired(true);
        setTwoFactorToken(result.twoFactorToken);
        setPendingUser(result.user || null);
        setError('');
        return;
      }
      
      // Kiểm tra role sau khi đăng nhập thành công (không có Face ID hoặc 2FA)
      if (authService.hasAdminAccess()) {
        navigate('/admin');
      } else {
        // Nếu không có quyền admin, đăng xuất và chuyển đến trang unauthorized
        authService.clearTokens();
        navigate('/unauthorized');
      }
    } catch (err: any) {
      // Hiển thị lỗi chi tiết từ backend
      console.error('Login error:', err);
      
      let displayErrorMessage = 'Tên đăng nhập hoặc mật khẩu không đúng'; // Default message

      if (err.response?.data) {
        const backendError = err.response.data;
        if (backendError.errorMessage) {
          displayErrorMessage = backendError.errorMessage;
        } else if (backendError.message) { // Fallback to 'message' if 'errorMessage' is not present
          displayErrorMessage = backendError.message;
        } else if (backendError.error) { // Fallback to 'error' if 'message' is not present
          displayErrorMessage = backendError.error;
        }
      } else if (err.message && err.message !== 'Login failed') {
        displayErrorMessage = err.message;
      }
      
      setError(displayErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    setError('');

    try {
      const result = await authService.verifyTwoFactorLogin({
        twoFactorToken,
        otp: useBackupCode ? undefined : otpCode.trim(),
        backupCode: useBackupCode ? backupCode.trim() : undefined
      });

      // Kiểm tra nếu cần Face ID verification
      if (result && 'requiresFaceId' in result && result.requiresFaceId) {
        setIsTwoFactorRequired(false);
        setIsFaceIdRequired(true);
        setFaceIdToken(result.faceIdToken);
        setIsFaceIdScanning(true);
        setError('');
        return;
      }

      if (authService.hasAdminAccess()) {
        resetTwoFactorState();
        navigate('/admin');
      } else {
        authService.clearTokens();
        navigate('/unauthorized');
      }
    } catch (err: any) {
      console.error('Two factor verification error:', err);
      let displayErrorMessage = 'Mã xác minh không hợp lệ';

      if (err.response?.data) {
        const backendError = err.response.data;
        if (backendError.errorMessage) {
          displayErrorMessage = backendError.errorMessage;
        } else if (backendError.message) {
          displayErrorMessage = backendError.message;
        } else if (backendError.error) {
          displayErrorMessage = backendError.error;
        }
      } else if (err.message && err.message !== 'Two-factor verification failed') {
        displayErrorMessage = err.message;
      }

      setError(displayErrorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const resetTwoFactorState = () => {
    setIsTwoFactorRequired(false);
    setTwoFactorToken('');
    setOtpCode('');
    setBackupCode('');
    setUseBackupCode(false);
    setPendingUser(null);
    setIsVerifying(false);
    setError('');
  };

  const resetFaceIdState = () => {
    setIsFaceIdRequired(false);
    setFaceIdToken('');
    setIsFaceIdScanning(false);
    setFaceIdBackupCode('');
    setUseFaceIdBackupCode(false);
    setError('');
  };

  const handleFaceDetected = async (descriptor: Float32Array) => {
    
    if (!faceIdToken) {
      setError('Thiếu Face ID token. Vui lòng thử lại.');
      return;
    }

    try {
      setIsVerifying(true);
      setError('');

      // Convert Float32Array to regular array
      const embedding = Array.from(descriptor);

      const result = await authService.faceId.verifyLogin(faceIdToken, embedding, undefined);

      // Kiểm tra nếu có yêu cầu 2FA (fallback từ Face ID fail)
      if (result && 'requiresTwoFactor' in result && result.requiresTwoFactor) {
        setIsFaceIdRequired(false);
        setIsTwoFactorRequired(true);
        setTwoFactorToken(result.twoFactorToken);
        setIsFaceIdScanning(false);
        setError('Face ID không khớp. Vui lòng sử dụng mã 2FA từ ứng dụng xác thực để tiếp tục.');
        return;
      }

      // Sau khi verify thành công, tokens và user đã được lưu trong authService.verifyFaceIdLogin
      // Nếu không có requiresTwoFactor, nghĩa là đã login thành công
      resetFaceIdState();
      navigate('/admin');
    } catch (err: any) {
      console.error('Face ID verification error:', err);
      setIsFaceIdScanning(false);
      
      // Kiểm tra response có yêu cầu 2FA không (trong error response)
      const responseData = err?.response?.data?.data;
      if (responseData && responseData.requiresTwoFactor) {
        setIsFaceIdRequired(false);
        setIsTwoFactorRequired(true);
        setTwoFactorToken(responseData.twoFactorToken);
        setError('Face ID không khớp. Vui lòng sử dụng mã 2FA từ ứng dụng xác thực để tiếp tục.');
        return;
      }
      
      // Nếu có hasTwoFactorBackup flag, hiển thị option chuyển sang 2FA
      const message = err?.message || err?.errorMessage || 'Xác thực Face ID thất bại. Vui lòng thử lại hoặc sử dụng mã bảo mật.';
      setError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFaceError = (error: string) => {
    setError(error);
    setIsFaceIdScanning(false);
  };

  const handleFaceIdBackupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!faceIdToken || !faceIdBackupCode) {
      setError('Vui lòng nhập mã bảo mật');
      return;
    }

    try {
      setIsVerifying(true);
      setError('');

      const result = await authService.faceId.verifyLogin(faceIdToken, undefined, faceIdBackupCode.trim().toUpperCase());

      // Kiểm tra nếu có yêu cầu 2FA (fallback từ Face ID fail)
      if (result && 'requiresTwoFactor' in result && result.requiresTwoFactor) {
        setIsFaceIdRequired(false);
        setIsTwoFactorRequired(true);
        setTwoFactorToken(result.twoFactorToken);
        setError('Face ID không khớp. Vui lòng sử dụng mã 2FA từ ứng dụng xác thực để tiếp tục.');
        return;
      }

      if (authService.hasAdminAccess()) {
        resetFaceIdState();
        navigate('/admin');
      } else {
        authService.clearTokens();
        navigate('/unauthorized');
      }
    } catch (err: any) {
      console.error('Face ID backup code verification error:', err);
      
      // Kiểm tra response có yêu cầu 2FA không
      const responseData = err?.response?.data?.data;
      if (responseData && responseData.requiresTwoFactor) {
        setIsFaceIdRequired(false);
        setIsTwoFactorRequired(true);
        setTwoFactorToken(responseData.twoFactorToken);
        setError('Face ID không khớp. Vui lòng sử dụng mã 2FA từ ứng dụng xác thực để tiếp tục.');
        return;
      }
      
      const message = err?.message || err?.errorMessage || 'Mã bảo mật không đúng. Vui lòng thử lại.';
      setError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSwitchToTwoFactor = async () => {
    if (!faceIdToken) {
      setError('Thiếu Face ID token. Vui lòng thử lại.');
      return;
    }

    try {
      setIsVerifying(true);
      setError('');

      const result = await authService.faceId.switchTo2FA(faceIdToken);

      if (result && 'requiresTwoFactor' in result && result.requiresTwoFactor) {
        setIsFaceIdRequired(false);
        setIsTwoFactorRequired(true);
        setTwoFactorToken(result.twoFactorToken);
        setIsFaceIdScanning(false);
        setError('');
        return;
      }
    } catch (err: any) {
      console.error('Switch to 2FA error:', err);
      
      // Lấy error message từ response
      let message = 'Không thể chuyển sang 2FA. Vui lòng thử lại.';
      
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.message) {
        message = err.message;
      } else if (err?.errorMessage) {
        message = err.errorMessage;
      }
      
      // Nếu token hết hạn, hướng dẫn user đăng nhập lại
      if (message.includes('hết hạn') || message.includes('không hợp lệ') || message.includes('Token')) {
        setError('Phiên xác thực đã hết hạn. Vui lòng đăng nhập lại.');
      } else {
        setError(message);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2A4B7C] to-[#00C897] flex items-center justify-center p-4">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-white/15 rounded-full animate-bounce"></div>
        <div className="absolute bottom-40 left-20 w-12 h-12 bg-white/20 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/10 rounded-full animate-bounce"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: '"Pacifico", serif' }}>
            StackWay
          </h1>
          <p className="text-white/80 text-lg">Admin Panel</p>
        </div>

        {/* Login / Two-factor Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#2A4B7C] to-[#00C897] rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-admin-line text-2xl text-white"></i>
            </div>
            <h2 className="text-2xl font-bold text-[#333333] mb-2">
              {isFaceIdRequired ? 'Xác thực Face ID' : isTwoFactorRequired ? 'Xác minh hai bước' : 'Đăng nhập Admin'}
            </h2>
            <p className="text-gray-600">
              {isFaceIdRequired
                ? 'Vui lòng quét khuôn mặt để xác thực danh tính hoặc sử dụng mã bảo mật'
                : isTwoFactorRequired
                ? 'Nhập mã OTP trong ứng dụng xác thực hoặc sử dụng mã dự phòng để hoàn tất đăng nhập'
                : 'Vui lòng đăng nhập bằng email để truy cập hệ thống quản trị'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-lg flex-shrink-0 mt-0.5"></i>
                <div className="flex-1">
                  <div className="text-sm whitespace-pre-wrap break-words">{error}</div>
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={isFaceIdRequired ? handleFaceIdBackupSubmit : isTwoFactorRequired ? handleTwoFactorSubmit : handleSubmit}
            className="space-y-6"
          >
            {isFaceIdRequired ? (
              <>
                {/* Info Banner - Cải thiện với gradient và animation */}
                <div className="relative bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl px-5 py-4 shadow-sm overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200/20 rounded-full -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-cyan-200/20 rounded-full -ml-12 -mb-12"></div>
                  <div className="relative flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-md">
                      <i className="ri-face-recognition-line text-xl text-white"></i>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold text-gray-800 mb-1">Xác thực Face ID</p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Vui lòng đưa khuôn mặt vào khung để xác thực. Đảm bảo có đủ ánh sáng và khuôn mặt rõ ràng.
                      </p>
                    </div>
                  </div>
                </div>

                {!useFaceIdBackupCode ? (
                  <>
                    {/* Face Scanner với container đẹp hơn */}
                    <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 shadow-inner">
                      <FaceIDScanner
                        isScanning={isFaceIdScanning}
                        onFaceDetected={handleFaceDetected}
                        onError={handleFaceError}
                      />
                      
                      {/* Scanning indicator */}
                      {isFaceIdScanning && !isVerifying && (
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 backdrop-blur-sm">
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                          <span>Đang quét khuôn mặt...</span>
                        </div>
                      )}
                    </div>

                    {isVerifying && (
                      <div className="flex flex-col items-center justify-center gap-3 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
                        <div className="relative">
                          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <i className="ri-face-recognition-line text-blue-600 text-lg"></i>
                          </div>
                        </div>
                        <p className="text-sm font-medium text-gray-700">Đang xác thực khuôn mặt...</p>
                        <p className="text-xs text-gray-500">Vui lòng đợi trong giây lát</p>
                      </div>
                    )}

                    {/* Action Buttons - Cải thiện với button styles đẹp hơn */}
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-wrap gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            setUseFaceIdBackupCode(true);
                            setIsFaceIdScanning(false);
                          }}
                          disabled={isVerifying}
                          className="flex-1 min-w-[140px] px-4 py-2.5 bg-white border-2 border-[#2A4B7C] text-[#2A4B7C] rounded-lg font-medium text-sm hover:bg-[#2A4B7C] hover:text-white transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-key-line text-base"></i>
                          <span>Mã bảo mật</span>
                        </button>

                        {hasTwoFactorBackup && (
                          <button
                            type="button"
                            onClick={handleSwitchToTwoFactor}
                            disabled={isVerifying}
                            className="flex-1 min-w-[140px] px-4 py-2.5 bg-gradient-to-r from-[#00C897] to-[#00a67a] text-white rounded-lg font-medium text-sm hover:from-[#00a67a] hover:to-[#008865] transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <i className="ri-shield-check-line text-base"></i>
                            <span>Mã 2FA</span>
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setError('');
                            setIsFaceIdScanning(true);
                            setIsVerifying(false);
                            setUseFaceIdBackupCode(false);
                          }}
                          disabled={isVerifying}
                          className="flex-1 min-w-[140px] px-4 py-2.5 bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-200 hover:border-gray-400 transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-refresh-line text-base"></i>
                          <span>Thử lại</span>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Backup Code Input - Cải thiện với styling đẹp hơn */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <i className="ri-key-line text-[#2A4B7C]"></i>
                        <span>Mã bảo mật (Backup Code)</span>
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-lock-password-line text-gray-400 group-focus-within:text-[#2A4B7C] transition-colors"></i>
                        </div>
                        <input
                          type="text"
                          value={faceIdBackupCode.replace(/(.{4})/g, '$1-').replace(/-\s*$/, '')}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase().replace(/[^0-9A-F]/g, '').slice(0, 8);
                            setFaceIdBackupCode(value);
                          }}
                          className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#2A4B7C] focus:border-[#2A4B7C] text-lg transition-all duration-300 uppercase font-mono tracking-wider text-center bg-gray-50 focus:bg-white shadow-sm hover:shadow-md"
                          placeholder="XXXX-XXXX"
                          maxLength={9}
                          required
                          disabled={isVerifying}
                        />
                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                          {faceIdBackupCode.length > 0 && (
                            <div className="flex gap-1">
                              {Array.from({ length: 8 }).map((_, i) => (
                                <div
                                  key={i}
                                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                    i < faceIdBackupCode.replace(/-/g, '').length
                                      ? 'bg-[#00C897]'
                                      : 'bg-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <i className="ri-information-line"></i>
                        <span>Nhập mã bảo mật 8 ký tự đã được cung cấp khi đăng ký Face ID</span>
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setUseFaceIdBackupCode(false);
                            setIsFaceIdScanning(true);
                            setFaceIdBackupCode('');
                            setError('');
                          }}
                          disabled={isVerifying}
                          className="flex-1 px-4 py-2.5 bg-white border-2 border-[#2A4B7C] text-[#2A4B7C] rounded-lg font-medium text-sm hover:bg-[#2A4B7C] hover:text-white transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-camera-line text-base"></i>
                          <span>Quét Face ID</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            resetFaceIdState();
                            setIsFaceIdRequired(false);
                            setPendingUser(null);
                            setError('');
                          }}
                          disabled={isVerifying}
                          className="flex-1 px-4 py-2.5 bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-200 hover:border-gray-400 transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-arrow-go-back-line text-base"></i>
                          <span>Đăng nhập lại</span>
                        </button>
                      </div>

                      <button
                        type="submit"
                        disabled={isVerifying || !faceIdBackupCode || faceIdBackupCode.replace(/-/g, '').length < 8}
                        className="w-full bg-gradient-to-r from-[#2A4B7C] to-[#00C897] hover:from-[#2A4B7C]/90 hover:to-[#00C897]/90 text-white py-3.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {isVerifying ? (
                          <div className="flex items-center justify-center gap-3">
                            <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Đang xác minh mã bảo mật...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2">
                            <i className="ri-shield-check-line text-lg"></i>
                            <span>Xác minh mã bảo mật</span>
                          </div>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : isTwoFactorRequired ? (
              <>
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-shield-check-line text-lg mt-0.5"></i>
                    <div className="text-sm text-left">
                      <p className="font-semibold">Tài khoản cần xác minh hai bước</p>
                      {pendingUser?.email && (
                        <p className="text-blue-700/80 mt-1 break-all">
                          Vui lòng nhập khóa bảo mật đã đăng ký vào ứng dụng xác thực để hoàn tất đăng nhập
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {!useBackupCode ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mã OTP
                    </label>
                    <div
                      className="grid grid-cols-6 gap-2"
                      onPaste={handleOtpPaste}
                    >
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div key={index} className="relative">
                          <input
                            ref={(el) => { otpInputRefs.current[index] = el; }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            className="w-full aspect-square text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent transition-all duration-200"
                            value={otpCode[index] || ''}
                            onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mã dự phòng
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="ri-lock-password-line text-gray-400"></i>
                      </div>
                      <input
                        type="text"
                        value={backupCode}
                        onChange={(e) => handleBackupCodeChange(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm transition-all duration-300 uppercase"
                        placeholder="Nhập mã dự phòng"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackupCode(!useBackupCode);
                      setOtpCode('');
                      setBackupCode('');
                      if (useBackupCode) {
                        setTimeout(() => otpInputRefs.current[0]?.focus(), 0);
                      }
                    }}
                    className="text-sm text-[#2A4B7C] hover:text-[#00C897] transition-colors inline-flex items-center gap-2 justify-center"
                  >
                    <i className="ri-refresh-line"></i>
                    {useBackupCode ? 'Dùng mã OTP thay thế' : 'Dùng mã dự phòng'}
                  </button>

                  <button
                    type="button"
                    onClick={resetTwoFactorState}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-2 justify-center"
                  >
                    <i className="ri-arrow-go-back-line"></i>
                    Đăng nhập lại
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isVerifying}
                  className="w-full bg-gradient-to-r from-[#2A4B7C] to-[#00C897] hover:from-[#2A4B7C]/90 hover:to-[#00C897]/90 text-white py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isVerifying ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Đang xác minh...</span>
                    </div>
                  ) : (
                    'Xác minh và đăng nhập'
                  )}
                </button>
              </>
            ) : (
              <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400"></i>
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm transition-all duration-300"
                  placeholder="Nhập email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mật khẩu
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent text-sm transition-all duration-300"
                  placeholder="Nhập mật khẩu"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-[#2A4B7C] to-[#00C897] hover:from-[#2A4B7C]/90 hover:to-[#00C897]/90 text-white py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Đang đăng nhập...</span>
                </div>
              ) : (
                'Đăng nhập'
              )}
            </button>
              </>
            )}
          </form>

         

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <a 
              href="/" 
              className="text-[#2A4B7C] hover:text-[#00C897] font-medium transition-colors cursor-pointer inline-flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              Quay lại trang chủ
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
